import { Inject } from '@odinjs/odin';
import { ButtonAPI, DateFieldAPI, FieldAPI, FormAPI, ModalService, PhoneNumberAPI } from '@philips-internal/oswald-ui-components';
import { Country } from '@philips-internal/oswald-ui-constants';
import { ContextType, Value } from '@philips-internal/oswald-ui-core';
import Controller, { OnBeforeSubmit, OnChange, OnClick, OnMount, OnValidate } from '@philips-internal/oswald-ui-core/lib/client';
import { SubmittableFragmentContext } from '@philips-internal/oswald-ui-core/lib/component/context';
import { AbortableSubmitEvent, ChangeEvent, MountEvent, SubmitEvent, ValidateEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import { LanguageService, MessageService, } from '@philips-internal/oswald-ui-core/lib/services';
import { Establishment, foundationContext } from '@philips-internal/oswald-ui-foundation';
import { ISO_DATE_FORMAT } from '@philips-internal/oswald-ui-model/lib/formatters/date/date-utils';
import moment from 'moment';
import { ScheduleWaitingListData } from '../../../app/types';
import { APPLICATION_ROUTES } from '../../../constants/application-routes';
import AppointmentService from '../../../services/appointment-service';
import { showToastNotificationError } from '../../../utils/toast-notification-utils';
import BaseController from '../../BaseController';
import './scheduling-waiting-list-controller.css';
import { SchedulingWaitingListAliases } from '../../../codegen';

interface UgencyReasonProps {
  dsReason: string;
  nrSequence: number;
}

interface MedicalSpecialityProps {
  cdEspecialidade: number;
  dsEspecialidade: string;
}

interface PhysicianProps {
  cdPessoaFisica?: string;
  nmPessoaFisica?: string;
}

interface ScheduleTypeProps {
  id: number;
  value: string;
}

interface CurrentPatientSelected {
  id: number;
  name: string;
}

interface FormDependencies {
  calendarFinalDateInitialRange: string;
  currentPatientSelected: CurrentPatientSelected;
  healthInsuranceCategoryId: string;
  healthInsuranceId: number;
  healthInsurancePlanId: string;
}

interface ExamScheduleProps {
  exam: {
    dsImagemBase64: string;
    dsProcInterno: string;
    dsTipoExame: string;
    nrSeqAgeintItem: string;
    nrSeqProcInterno: number;
  }
}

interface SpecialityDataProps {
  cdEspecialidade?: number;
}

interface ConsultScheduleProps {
  nrSeqAgeintItem?: string;
  physician?: PhysicianProps,
  specialityData?: SpecialityDataProps;
}

export interface WaitingListData {
  availablePeriod: string[];
  consultScheduleList: ConsultScheduleProps[];
  dependencies: FormDependencies;
  examScheduleList: ExamScheduleProps[];
  medicalSpecialities: MedicalSpecialityProps[];
  nrSeqAgeint: number;
  nrSeqAgeintItem: string;
  physician?: PhysicianProps;
  scheduleType: ScheduleTypeProps[];
  urgencyReason?: UgencyReasonProps;
}

interface WaitingListDataForm extends WaitingListData {
  contactName: string;
  contactNumber: string;
  desiredDate: string;
  dsNote: string;
  establishment: Establishment;
  ieUrgent: boolean;
  nrSeqAgeintItem: string;
}

type ScheduleType = 'CONSULTATION' | 'EXAM';

interface ScheduleTypeData {
  nrSeqAgeintItem: string;
  nrSeqProcInterno?: string;
  physicianId: string;
  specialityId: string;
  type: ScheduleType;
}

@Controller({fragment: 'scheduling-waiting-list'})
export default class SchedulingWaitingListController extends BaseController {

  @Inject('form-waiting-list')
    formWaitingListApi!: FormAPI;

  @Inject('field-urgency-reason')
    fieldUrgencyReasonApi!: FieldAPI;

  @Inject('button-confirm')
    buttonConfirmApi!: ButtonAPI;

  @Inject('field-exams')
    fieldExamsApi!: FieldAPI;

  @Inject(ContextType.SUBMITTABLE_FRAGMENT)
    submittableFragmentContext!: SubmittableFragmentContext;

  @Inject('field-available-period')
    fieldAvailablePeriodApi!: FieldAPI;

  @Inject('field-desired-date')
    fieldDesiredDateApi!: FieldAPI;

  @Inject('field-available-period-readonly')
    fieldAvailablePeriodReadonlyApi!: FieldAPI;

  @Inject('date-field-desired-date')
    dateFieldDesiredDateApi!: DateFieldAPI;

  @Inject('date-field-available-period')
    dateFieldAvailablePeriodApi!: DateFieldAPI;

  @OnValidate({target: 'form-waiting-list'})
  handleValidateForm(event: ValidateEvent): void {
    this.buttonConfirmApi.setEnabled(event.valid);
  }

  @OnMount({target: ['date-field-available-period', 'date-field-desired-date']})
  handleDateFieldAvailablePeriodLoading(event: MountEvent): void {
    const dateField = event.target as DateFieldAPI;

    const waitingListData = this.context.get('waitingListData') as WaitingListData;
    const today = moment().utc().toISOString();
    dateField.addRange(today, waitingListData.dependencies.calendarFinalDateInitialRange);
  }

  @OnChange({ target: SchedulingWaitingListAliases.DATE_FIELD_AVAILABLE_PERIOD })
  async handleDateFieldAvailablePeriodChange(event: ChangeEvent<string[]>): Promise<void> {
    const selectedPeriod = event.value;
    this.dateFieldDesiredDateApi.clearRanges();
    if(selectedPeriod.length === 0) {
      const today = moment().utc().toISOString();
      const waitingListData = this.context.get('waitingListData') as WaitingListData;
      this.dateFieldDesiredDateApi.addRange(today, waitingListData.dependencies.calendarFinalDateInitialRange);
      return;
    }
    this.dateFieldDesiredDateApi.addRange(selectedPeriod[0], selectedPeriod[1]);
    const currentDesiredDate = this.fieldDesiredDateApi.getValue() as string || '';
    const currentDesiredDateAsMoment = moment(currentDesiredDate, ISO_DATE_FORMAT, true);
    const currentDesiredDateisValid = currentDesiredDateAsMoment.isValid();
    if(!currentDesiredDateisValid || !currentDesiredDateAsMoment.isBetween(selectedPeriod[0], selectedPeriod[1], undefined, '[]')) {
      this.fieldDesiredDateApi.setValue('');
      await this.fieldDesiredDateApi.validate();
    }
  }

  @OnChange({target: SchedulingWaitingListAliases.DATE_FIELD_DESIRED_DATE })
  async handleDateFieldDesiredDateChange(event: ChangeEvent<string>): Promise<void> {
    const dateSelected = event.value;
    const dateSelectedAsMoment = moment(dateSelected, ISO_DATE_FORMAT, true);
    const waitingListData = this.context.get('waitingListData') as WaitingListData;
    const dateFieldAvailablePeriodData = this.fieldAvailablePeriodApi.isVisible() ? this.dateFieldAvailablePeriodApi.getValue() : waitingListData.availablePeriod;
    const doesNotExistsSelectedPeriod = !dateFieldAvailablePeriodData;

    if(doesNotExistsSelectedPeriod && dateSelected && dateSelectedAsMoment.isValid()) {
      const validDateFieldRange = this.dateFieldDesiredDateApi.getRanges();
      if(!dateSelectedAsMoment.isBetween(validDateFieldRange[0].startDate, validDateFieldRange[0].endDate, 'date', '[]')) {
        this.fieldDesiredDateApi.setValue('');
        await this.fieldDesiredDateApi.validate();
        return;
      }

      this.dateFieldDesiredDateApi.clearRanges();
      this.dateFieldDesiredDateApi.addRange(dateSelected, dateSelected);
      this.fieldAvailablePeriodApi.setValue([dateSelected, dateSelected]);
      await this.fieldAvailablePeriodApi.validate();
    }

    if((dateSelectedAsMoment.isValid() && dateFieldAvailablePeriodData && dateFieldAvailablePeriodData.length > 0) &&
      !(dateSelectedAsMoment.isBetween(dateFieldAvailablePeriodData[0], dateFieldAvailablePeriodData[1], 'date', '[]'))) {
      // Don't remove. Solution for component validation bug.
      // Without this block, component validation is not performed correctly.
      // When the Form's onValidate is called it indicates that the component is already valid, and this is wrong.
      // Start block
      const fieldValidators = [...this.fieldDesiredDateApi.getValidators()];
      this.fieldDesiredDateApi.removeValidator('required');
      this.fieldDesiredDateApi.removeValidator('date');
      this.fieldDesiredDateApi.setValue('');
      this.fieldDesiredDateApi.setValidators(fieldValidators);
      await this.fieldDesiredDateApi.validate();
      await this.formWaitingListApi.validate();
      // End block
    }
  }

  @OnMount({target: 'form-waiting-list'})
  handleFormLoading(event: MountEvent): void {
    const formAPI = event.target as FormAPI;
    const waitingListData = this.context.get('waitingListData') as WaitingListData;
    const consultScheduleList = waitingListData.consultScheduleList;
    const specialityId = consultScheduleList.length > 0 ? consultScheduleList[0].specialityData?.cdEspecialidade : null;
    const medicalSpeciality = consultScheduleList.length > 0 ? waitingListData.medicalSpecialities.find(speciality => speciality.cdEspecialidade === specialityId) : {};

    const { availablePeriod, scheduleType, physician, examScheduleList } = waitingListData;
    let availablePeriodReadonly = '';
    if(availablePeriod && availablePeriod.length > 0) {
      this.fieldAvailablePeriodApi.setVisible(false);
      this.fieldAvailablePeriodReadonlyApi.setVisible(true);
      availablePeriodReadonly = `${moment.utc(availablePeriod[0]).format('L')} - ${moment.utc(availablePeriod[1]).format('L')}`;

      this.dateFieldDesiredDateApi.clearRanges();
      this.dateFieldDesiredDateApi.addRange(availablePeriod[0], availablePeriod[1]);
    }

    if(scheduleType.length === 1 && scheduleType[0].value === 'schedule-type.consultation') {
      this.fieldExamsApi.setVisible(false);
    }

    const physicianNameSplited = physician?.nmPessoaFisica?.split('-') || [];
    if(physician && physicianNameSplited?.length === 2) {
      physician.nmPessoaFisica = physicianNameSplited[1].trim();
    }

    const { id, name } = foundationContext.getEstablishment() as Establishment;
    const establishment = {id, name};

    const exam = examScheduleList && examScheduleList.length > 0 ? examScheduleList[0].exam : undefined;
    formAPI.setData({
      availablePeriod: waitingListData.availablePeriod,
      availablePeriodReadOnly: availablePeriodReadonly,
      establishment: establishment,
      exam: exam,
      medicalSpeciality: medicalSpeciality,
      physician: waitingListData.physician,
      scheduleType: waitingListData.scheduleType,
    });
  }

  @OnMount({target: 'phone-number-contact'})
  handleContactNumberLoading(event: MountEvent): void {
    const phoneCountry = LanguageService.getCurrentLanguage().split('-')[1].toLocaleUpperCase();
    const phoneNumber = event.target as PhoneNumberAPI;
    phoneNumber.setCountry(phoneCountry as Country);
  }

  @OnClick({target: 'button-confirm'})
  handleConfirmButtonClick(): void {
    this.formWaitingListApi.submit();
  }

  @OnClick({target: 'button-cancel'})
  handleCancelButtonClick(): void {
    this.submittableFragmentContext.cancel(new SubmitEvent());
  }

  @OnBeforeSubmit({target: 'form-waiting-list'})
  handleFormSubmit(event: AbortableSubmitEvent): void {
    const record = event.record as WaitingListDataForm;

    let type: ScheduleType = '' as ScheduleType;
    const waitingListData = this.context.get('waitingListData') as WaitingListData;
    const scheduleType = waitingListData.scheduleType[0];
    switch (scheduleType.value) {
      case 'schedule-type.consultation':
        type = 'CONSULTATION'
        break;
      case 'schedule-type.exam':
        type = 'EXAM';
        break;
      default:
        break;
    }

    let scheduleTypeData: ScheduleTypeData = {} as ScheduleTypeData;

    if(type === 'CONSULTATION') {
      const consult = waitingListData.consultScheduleList[0];
      scheduleTypeData = {
        nrSeqAgeintItem: consult.nrSeqAgeintItem || waitingListData.nrSeqAgeintItem,
        physicianId: consult.physician?.cdPessoaFisica || '',
        specialityId: String(consult.specialityData?.cdEspecialidade || ''),
        type: type
      }
    } else if(type === 'EXAM') {
      const exam = waitingListData.examScheduleList[0].exam;
      scheduleTypeData = {
        nrSeqAgeintItem: exam.nrSeqAgeintItem || '',
        nrSeqProcInterno: String(exam.nrSeqProcInterno || ''),
        physicianId: '',
        specialityId: '',
        type: type
      }
    } else {
      throw new Error('The type entered does not exist or has not been mapped.');
    }

    const startAt = record.availablePeriod == null || record.availablePeriod.length == 0 ? null : record.availablePeriod[0];
    const endAt = startAt != null && record.availablePeriod.length === 2 ? record.availablePeriod[1] : startAt;

    const body: ScheduleWaitingListData = {
      availableType: 'W',
      cdCategoria: waitingListData.dependencies.healthInsuranceCategoryId,
      cdConvenio: waitingListData.dependencies.healthInsuranceId,
      cdEspecialidade: scheduleTypeData.specialityId,
      cdEstabelecimento: record.establishment.id,
      cdMedico: scheduleTypeData.physicianId,
      cdPessoaFisica: waitingListData.dependencies.currentPatientSelected.id,
      cdPlano: waitingListData.dependencies.healthInsurancePlanId,
      contactName: record.contactName,
      contactNumber: record.contactNumber,
      desiredDate: record.desiredDate,
      dsNota: record.dsNote,
      dtAgenda: record.desiredDate,
      endAt: endAt,
      ieUrgent: record.ieUrgent,
      nrSeqAgeint: waitingListData.nrSeqAgeint,
      nrSeqAgeintItem: scheduleTypeData.nrSeqAgeintItem,
      nrSeqProcInterno: scheduleTypeData.nrSeqProcInterno,
      startAt: startAt,
      type: scheduleTypeData.type,
      urgencyReasonId: record.urgencyReason?.nrSequence,
    }

    const haveToReturnWaitingListItem = this.context.get('haveToReturnWaitingListItem') as boolean;
    if(haveToReturnWaitingListItem) {
      this.submittableFragmentContext.submit(new SubmitEvent({ record: body }));
      return;
    }

    this.buttonConfirmApi.setEnabled(false);
    this.formWaitingListApi.setLoading(true);
    AppointmentService.confirmAndRequestAppointments(body.nrSeqAgeint, body.cdPessoaFisica, [body] as unknown as Record<string, Value>[]).then(bodyContent => {
      this.buttonConfirmApi.setEnabled(false);
      ModalService.openFullscreen({
        className: 'schedule-confirmation-modal-custom-bg',
        closeable: true,
        contextData: bodyContent,
        dockButtons: [],
        fragment: 'schedule-confirmation',
        onClose: () => {
          this.submittableFragmentContext.cancel(new SubmitEvent());
          this.historyService.push(APPLICATION_ROUTES.PSEUDONYM_APPOINTMENTS, {}, {scrollToTop: true});
        },
        title: MessageService.translate('patient.schedule-success'),
      })
    }).catch(() => {
      this.buttonConfirmApi.setEnabled(true);
      this.formWaitingListApi.setEditing(true);
      showToastNotificationError(MessageService.translate('patient.appointments.confirm-failed'));
    }).finally(() => {
      this.formWaitingListApi.setLoading(false)
    });
  }

  @OnChange({ target: SchedulingWaitingListAliases.FIELD_IE_URGENT })
  async handleChangeFieldIeUrgent(event: ChangeEvent<Record<string, Value>>): Promise<void> {
    const showUrgencyReason = event.value  as unknown as boolean;
    this.fieldUrgencyReasonApi.setVisible(showUrgencyReason);

    if(showUrgencyReason) {
      this.fieldUrgencyReasonApi.addValidator({name: 'required', options: {}});
      await this.fieldUrgencyReasonApi.validate();
    } else {
      this.fieldUrgencyReasonApi.removeValidator('required');
      await this.fieldUrgencyReasonApi.validate();
    }
  }
}
